import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const empty = { product_id: "", discount_percent: 10, label: "Special Offer", label_ta: "", is_active: true, valid_until: "" };

export default function OffersPanel() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [o, p] = await Promise.all([api.get("/offers"), api.get("/products")]);
    setItems(o.data || []);
    setProducts(p.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.product_id) return toast.error("Pick a product");
    try {
      const payload = {
        ...form,
        discount_percent: Number(form.discount_percent),
        valid_until: form.valid_until || null,
      };
      if (editingId) {
        await api.put(`/offers/${editingId}`, payload);
        toast.success("Offer updated");
      } else {
        await api.post("/offers", payload);
        toast.success("Offer created");
      }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };

  const edit = (o) => {
    setForm({
      product_id: o.product_id,
      discount_percent: o.discount_percent,
      label: o.label || "",
      label_ta: o.label_ta || "",
      is_active: o.is_active ?? true,
      valid_until: o.valid_until || "",
    });
    setEditingId(o.id);
    setOpen(true);
  };

  const toggle = async (o) => {
    await api.put(`/offers/${o.id}`, { ...o, is_active: !o.is_active });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete offer?")) return;
    await api.delete(`/offers/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-xl font-semibold text-charcoal">Offers</h2>
          <p className="text-sm text-charcoal/60">Attach discounts to individual SKUs</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-offer-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>
              <Plus className="w-4 h-4 mr-1.5" /> New offer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit offer" : "Create offer"}</DialogTitle>
              <DialogDescription>Discounts apply to a single SKU. Optional expiry hides the offer automatically.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Product</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                  <SelectTrigger data-testid="offer-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount (%)</Label>
                <Input type="number" step="0.1" min="0" max="90" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="offer-discount" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label (English)</Label>
                  <Input data-testid="offer-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                </div>
                <div>
                  <Label>பெயர் (Tamil, optional)</Label>
                  <Input data-testid="offer-label-ta" value={form.label_ta} onChange={(e) => setForm({ ...form, label_ta: e.target.value })} placeholder="சிறப்பு சலுகை" />
                </div>
              </div>
              <div>
                <Label>Valid until (optional)</Label>
                <Input type="datetime-local" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="save-offer-btn" style={{ backgroundColor: "var(--forest)", color: "white" }}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-black/10 rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Valid until</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-charcoal/50 py-8">No offers yet</TableCell></TableRow>
            )}
            {items.map((o) => (
              <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                <TableCell className="font-medium">{o.product_name || o.product_id}</TableCell>
                <TableCell>
                  <span className="offer-shine inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full">
                    <Sparkles className="w-3 h-3" /> {Number(o.discount_percent)}% Off
                  </span>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{o.label}</div>
                  {o.label_ta && <div className="text-xs text-charcoal/60">{o.label_ta}</div>}
                </TableCell>
                <TableCell><Switch checked={!!o.is_active} onCheckedChange={() => toggle(o)} /></TableCell>
                <TableCell className="font-mono text-xs">{o.valid_until ? new Date(o.valid_until).toLocaleString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => edit(o)} data-testid={`edit-offer-${o.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(o.id)}><Trash2 className="w-3.5 h-3.5 text-terracotta" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
